// People — company members list via POST /company-users/users (server-side
// pagination). Item shape per the confirmed response (2026-09-26 sample):
// projects as {_id, title}, structure nodes WITHOUT ids, and profile fields
// split across two levels — nationality/birthDate/lastLoginDate belong to the
// nested `user` schema, the rest sit at the item level. Still unconfirmed:
// the groups/tags element shapes (observed only as empty arrays).
// Normalization is isolated in toPerson so any drift is a one-file fix.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

/** Confirmed wire contract for a company user's `userStatus` — the backend's
 *  full lifecycle set. There is no `inactive` or `declined` value. */
export enum CompanyUserStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  PENDING = 'pending',
  ADMIN_INVITE = 'admin_invite',
  BLOCKED = 'blocked',
}

export interface PersonListItem {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  roles: { id: string; name: string }[];
  /** True when the person holds the company owner role — bulk role actions are
   *  disabled for owners. Always false for guests. */
  isOwner: boolean;
  /** Structure placements — the level's name + icon ride along on each node. */
  units: { id: string; name: string; levelName: string; levelIcon: string }[];
  projects: { id: string; title: string; icon: string }[];
  groups: string[];
  tags: { id: string; title: string }[];
  nationality: string;
  // Profile fields (same names as the detail/create contracts), all optional
  // on the wire; '' when the list payload doesn't carry them. Dates are ISO.
  department: string;
  employmentType: string;
  personalIdNumber: string;
  personalIdExpiry: string;
  birthDate: string;
  status: CompanyUserStatus;
  lastLoginDate: string | null;
  // Raw phone parts — only guests populate these (to prefill the edit form,
  // since `phone` above is a combined display string).
  countryCode?: string;
  mobileNumber?: string;
}

export interface PeoplePage {
  items: PersonListItem[];
  total: number;
}

/** POST /company-users/users body. `userStatus` carries exactly the active
 *  tab's lifecycle value(s) — e.g. Pending sends [PENDING, ADMIN_INVITE]. */
export interface PeopleQueryArgs {
  page: number;
  limit: number;
  search?: string;
  userStatus: CompanyUserStatus[];
  // Toolbar filter selections — id arrays sent verbatim in the POST body.
  // Omitted (undefined) when the matching filter section has no selection.
  roles?: string[];
  groups?: string[];
  tags?: string[];
  structureNodes?: string[];
  // Literal display-string values (the personFields.ts lists, stored verbatim).
  employmentType?: string[];
  nationality?: string[];
}

/** POST /guests/all body — same search/pagination semantics as the people
 *  list, but guests carry no lifecycle status and no toolbar filters. */
export interface GuestsQueryArgs {
  page: number;
  limit: number;
  search?: string;
}

/** POST /guests body — flat identity fields. `email` + `countryCode`/
 *  `mobileNumber` are individually optional; the "email or phone" rule is
 *  enforced in the form, not here. */
export interface CreateGuestInput {
  firstName: string;
  lastName: string;
  email?: string;
  countryCode?: string;
  mobileNumber?: string;
}

// ---- raw shapes (per the confirmed response) ----
// The LIST payload's nodes are bare display facts — no _id on the node or its
// level (confirmed sample). The detail response shares this type and may still
// carry ids, so they stay optional rather than gone.
interface ApiStructureNode {
  _id?: string;
  name: string;
  level?: { _id?: string; name: string; icon: string } | null;
}
interface ApiPerson {
  _id: string;
  user: {
    _id?: string;
    firstName: string;
    lastName: string;
    email?: string | null;
    countryCode: string;
    mobileNumber: string;
    status?: string;
    // Part of the `user` schema, not the item level.
    nationality?: string | null;
    birthDate?: string | null;
    lastLoginDate?: string | null;
  };
  companyStructureNodes?: ApiStructureNode[] | null;
  userRoles?: Array<{ _id: string; name: string; isOwner?: boolean }> | null;
  userStatus?: CompanyUserStatus | null;
  // Confirmed in the list payload: projects are {_id, title}; `icon` is
  // optional (absent in the sample — honored if/when the wire carries it).
  // groups/tags are present but observed only as empty arrays, so their
  // element mapping stays defensive until a populated sample confirms it.
  projects?: Array<{ _id: string; title: string; icon?: string }> | null;
  groups?: Array<{ _id: string; name?: string; groupName?: string }> | null;
  tags?: Array<{ _id: string; title: string }> | null;
  // Optional profile fields — confirmed at the item level, same names as the
  // detail/create contracts (absent/null when the person has no value).
  department?: string | null;
  employmentType?: string | null;
  personalIdNumber?: string | null;
  personalIdExpiry?: string | null;
}
type ApiPeoplePage = { items?: ApiPerson[]; totalRecords?: number; total?: number };

const toPerson = (p: ApiPerson): PersonListItem => ({
  id: p._id,
  firstName: p.user.firstName,
  lastName: p.user.lastName,
  fullName: `${p.user.firstName} ${p.user.lastName}`.trim(),
  email: p.user.email ?? '',
  phone: `${p.user.countryCode} ${p.user.mobileNumber}`,
  roles: (p.userRoles ?? []).map((r) => ({ id: r._id, name: r.name })),
  isOwner: (p.userRoles ?? []).some((r) => !!r.isOwner),
  // List nodes carry no _id (display-only chips; nothing keys off unit ids here).
  units: (p.companyStructureNodes ?? []).map((n) => ({
    id: n._id ?? '',
    name: n.name,
    levelName: n.level?.name ?? '',
    levelIcon: n.level?.icon ?? '',
  })),
  // Project icon is optional on the wire — stock glyph when absent.
  projects: (p.projects ?? []).map((pr) => ({
    id: pr._id,
    title: pr.title,
    icon: pr.icon ?? 'folder-kanban',
  })),
  groups: (p.groups ?? []).map((g) => g.name ?? g.groupName ?? '').filter(Boolean),
  tags: (p.tags ?? []).map((t) => ({ id: t._id, title: t.title })),
  nationality: p.user.nationality ?? '',
  department: p.department ?? '',
  employmentType: p.employmentType ?? '',
  personalIdNumber: p.personalIdNumber ?? '',
  personalIdExpiry: p.personalIdExpiry ?? '',
  birthDate: p.user.birthDate ?? '',
  status: p.userStatus ?? CompanyUserStatus.ACTIVE,
  lastLoginDate: p.user.lastLoginDate ?? null,
});

// ---- guest raw shapes (POST /guests/all — mapped independently of the
// people list so the two contracts can drift without touching each other) ----
interface ApiGuest {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  countryCode?: string | null;
  mobileNumber?: string | null;
}
type ApiGuestsPage = { items?: ApiGuest[]; totalRecords?: number; total?: number };

// Guests carry only identity data (flat, not nested under `user`), with either
// an email or a mobile number. The rest of the row shape stays empty so the
// trimmed Person + Phone columns render from the same PersonListItem.
const toGuest = (g: ApiGuest): PersonListItem => ({
  id: g._id,
  firstName: g.firstName,
  lastName: g.lastName,
  fullName: `${g.firstName} ${g.lastName}`.trim(),
  email: g.email ?? '',
  phone: g.mobileNumber ? `${g.countryCode ?? ''} ${g.mobileNumber}`.trim() : '',
  roles: [],
  isOwner: false,
  units: [],
  projects: [],
  groups: [],
  tags: [],
  nationality: '',
  department: '',
  employmentType: '',
  personalIdNumber: '',
  personalIdExpiry: '',
  birthDate: '',
  status: CompanyUserStatus.ACTIVE,
  lastLoginDate: null,
  countryCode: g.countryCode ?? undefined,
  mobileNumber: g.mobileNumber ?? undefined,
});

/** One person in the `POST /company-users/invite-users` batch. `firstName`/`lastName`
 *  are separate; `roles`/`companyStructureNodes`/`tags` are id arrays; the rest
 *  of the profile fields are optional. Dates are ISO strings. */
export interface CreatePersonInput {
  firstName: string;
  lastName: string;
  countryCode: string;
  mobileNumber: string;
  email?: string;
  roles: string[];
  companyStructureNodes: string[];
  tags: string[];
  personalIdNumber?: string;
  personalIdExpiry?: string;
  nationality?: string;
  birthDate?: string;
  department?: string;
  employmentType?: string;
}

// ---- person detail (GET /company-users/users/detail/:id) ----
// One access module = a feature area (moduleId) with the permissions granted on
// it. The Effective-access matrix is computed client-side in the drawer from the
// roles-selection-detailed endpoint — it is NOT part of this response.
export interface AccessModule {
  id: string;
  title: string;
  permissions: { id: string; title: string }[];
}

export interface PersonDetail {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  // Raw phone parts (email display uses `phone`); kept so the edit form can show
  // them, though phone is read-only in the current edit pass.
  countryCode: string;
  mobileNumber: string;
  status: CompanyUserStatus;
  department: string;
  employmentType: string;
  personalIdNumber: string;
  personalIdExpiry: string;
  nationality: string;
  birthDate: string;
  groups: { id: string; name: string }[];
  tags: { id: string; title: string }[];
  units: { id: string; name: string; levelName: string; levelIcon: string }[];
  roles: { id: string; name: string; isOwner: boolean }[];
}

interface ApiUserRole {
  _id: string;
  name: string;
  isOwner?: boolean;
}
interface ApiPersonDetail {
  _id: string;
  user: {
    _id?: string;
    firstName: string;
    lastName: string;
    email?: string | null;
    countryCode: string;
    mobileNumber: string;
    status?: string;
    // Part of the `user` schema, not the data level.
    nationality?: string | null;
    birthDate?: string | null;
  };
  companyStructureNodes?: ApiStructureNode[] | null;
  groups?: Array<{ _id: string; name?: string; groupName?: string }> | null;
  tags?: Array<{ _id: string; title: string }> | null;
  userRoles?: ApiUserRole[] | null;
  userStatus?: CompanyUserStatus | null;
  // Profile fields at the data level — nationality/birthDate ride in `user`.
  personalIdNumber?: string | null;
  personalIdExpiry?: string | null;
  department?: string | null;
  employmentType?: string | null;
}

const toPersonDetail = (d: ApiPersonDetail): PersonDetail => {
  const roles = d.userRoles ?? [];

  return {
    id: d._id,
    firstName: d.user.firstName,
    lastName: d.user.lastName,
    fullName: `${d.user.firstName} ${d.user.lastName}`.trim(),
    email: d.user.email ?? '',
    phone: d.user.mobileNumber ? `${d.user.countryCode} ${d.user.mobileNumber}`.trim() : '',
    countryCode: d.user.countryCode ?? '',
    mobileNumber: d.user.mobileNumber ?? '',
    status: d.userStatus ?? CompanyUserStatus.ACTIVE,
    department: d.department ?? '',
    employmentType: d.employmentType ?? '',
    personalIdNumber: d.personalIdNumber ?? '',
    personalIdExpiry: d.personalIdExpiry ?? '',
    nationality: d.user.nationality ?? '',
    birthDate: d.user.birthDate ?? '',
    groups: (d.groups ?? [])
      .map((g) => ({ id: g._id, name: g.name ?? g.groupName ?? '' }))
      .filter((g) => g.id && g.name),
    tags: (d.tags ?? []).map((tg) => ({ id: tg._id, title: tg.title })),
    // Unlike the list, the detail's nodes DO carry _id (structure editing keys
    // off it); the coalesce only satisfies the shared, list-loosened type.
    units: (d.companyStructureNodes ?? []).map((n) => ({
      id: n._id ?? '',
      name: n.name,
      levelName: n.level?.name ?? '',
      levelIcon: n.level?.icon ?? '',
    })),
    roles: roles.map((r) => ({ id: r._id, name: r.name, isOwner: r.isOwner ?? false })),
  };
};

/** PATCH company-users/users/:id body. Roles/nodes are editable id arrays sent
 *  from the drawer's draft (owner roles stay fixed); tags/groups likewise;
 *  `userStatus` echoes the person's current status unchanged (the drawer does
 *  not edit status). Email/phone are not sent (read-only). */
export interface UpdatePersonInput {
  firstName: string;
  lastName: string;
  userRoles: string[];
  companyStructureNodes: string[];
  tags: string[];
  groups: string[];
  userStatus: CompanyUserStatus;
  // Optional profile fields — sent as null when cleared/empty.
  department: string | null;
  employmentType: string | null;
  personalIdNumber: string | null;
  personalIdExpiry: string | null;
  nationality: string | null;
  birthDate: string | null;
}

/** Bulk operations applied to a set of selected company users. */
export enum CompanyUserBulkActionType {
  ASSIGN_ROLE = 'assign_role',
  REMOVE_ROLE = 'remove_role',
  ADD_TO_STRUCTURE = 'add_to_structure',
  // NOTE: backend's exact (typo'd) enum value — must match verbatim.
  REMOVE_FROM_STRUCTURE = 'remove_form_structure',
  ARCHIVE = 'archive',
  RESTORE = 'restore',
}

/** How Assign role applies: add to existing roles, or replace them. */
export type BulkActionMethod = 'add' | 'replace';

export interface BulkActionInput {
  companyUserIds: string[];
  actionType: CompanyUserBulkActionType;
  /** Role ids / structure-node ids the action targets ([] for archive/restore). */
  actionIds: string[];
  /** Only sent for Assign role (add vs replace). */
  actionMethod?: BulkActionMethod;
}

/** Invite lifecycle values the backend accepts. ACCEPTED/REJECTED decide a
 *  `pending` request; DELETED withdraws an `admin_invite`. */
export type InviteStatus = 'ACCEPTED' | 'REJECTED' | 'DELETED';

/** Accept/reject pending requests or withdraw admin invites. Bulk-capable, so
 *  the Pending tab's row kebab and its selection bar are the same call with a
 *  one- or many-id array. */
export interface UpdateInviteStatusInput {
  companyUserIds: string[];
  status: InviteStatus;
}

/** Permanently delete archived company users — same one-or-many shape. */
export interface DeleteCompanyUsersInput {
  companyUserIds: string[];
}

export const peopleApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getPeople: build.query<PeoplePage, PeopleQueryArgs>({
      query: (body) => ({ ...ROUTES.companyUsers.list, data: body }),
      transformResponse: (res: ApiPeoplePage) => {
        const items = res?.items ?? [];
        return {
          items: items.map(toPerson),
          total: res?.totalRecords ?? res?.total ?? items.length,
        };
      },
      providesTags: [{ type: 'People', id: 'LIST' }],
    }),
    // Guest list — separate endpoint with its own raw types + mapper.
    getGuests: build.query<PeoplePage, GuestsQueryArgs>({
      query: (body) => ({ ...ROUTES.guests.all, data: body }),
      transformResponse: (res: ApiGuestsPage) => {
        const items = res?.items ?? [];
        return {
          items: items.map(toGuest),
          total: res?.totalRecords ?? res?.total ?? items.length,
        };
      },
      providesTags: [{ type: 'People', id: 'GUEST_LIST' }],
    }),
    // Add person — POST /company-users/invite-users takes a `users` batch; we send one.
    createPerson: build.mutation<unknown, CreatePersonInput>({
      query: (user) => ({ ...ROUTES.companyUsers.create, data: { users: [user] } }),
      invalidatesTags: [{ type: 'People', id: 'LIST' }],
    }),
    // CSV import — POST /company-users/import-users with every ready row in one
    // `users` batch (same per-user shape as invite-users).
    importUsers: build.mutation<unknown, { users: CreatePersonInput[] }>({
      query: (body) => ({ ...ROUTES.companyUsers.importUsers, data: body }),
      invalidatesTags: [{ type: 'People', id: 'LIST' }],
    }),
    // Person profile — GET /company-users/users/detail/:id (identity + structure
    // + roles, with effective access computed from the roles' permissions).
    getPersonDetail: build.query<PersonDetail, string>({
      query: (id) => ({ ...ROUTES.companyUsers.detail(id) }),
      transformResponse: (res: ApiPersonDetail) => toPersonDetail(res),
      providesTags: (_res, _err, id) => [{ type: 'People', id: `DETAIL_${id}` }],
    }),
    // Edit person — PATCH /company-users/users/:id. Refetches the list + detail
    // so the drawer re-seeds from the server after a save.
    updatePerson: build.mutation<unknown, { id: string; body: UpdatePersonInput }>({
      query: ({ id, body }) => ({ ...ROUTES.companyUsers.update(id), data: body }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'People', id: 'LIST' },
        { type: 'People', id: `DETAIL_${id}` },
      ],
    }),
    // Add guest — POST /guests takes the flat identity body directly.
    createGuest: build.mutation<unknown, CreateGuestInput>({
      query: (guest) => ({ ...ROUTES.guests.create, data: guest }),
      invalidatesTags: [{ type: 'People', id: 'GUEST_LIST' }],
    }),
    // Guest detail — GET /guests/:id/details, same item shape as the list (via toGuest).
    // Fetched fresh to prefill the edit form rather than forwarding the row.
    getGuestDetail: build.query<PersonListItem, string>({
      query: (id) => ({ ...ROUTES.guests.detail(id) }),
      transformResponse: (res: ApiGuest) => toGuest(res),
      providesTags: (_res, _err, id) => [{ type: 'People', id: `GUEST_${id}` }],
    }),
    // Edit guest — PATCH /guests/:id with the same flat body.
    updateGuest: build.mutation<unknown, { id: string; body: CreateGuestInput }>({
      query: ({ id, body }) => ({ ...ROUTES.guests.update(id), data: body }),
      invalidatesTags: (_res, _err, { id }) => [
        { type: 'People', id: 'GUEST_LIST' },
        { type: 'People', id: `GUEST_${id}` },
      ],
    }),
    // Delete guest — DELETE /guests/:id.
    deleteGuest: build.mutation<unknown, string>({
      query: (id) => ({ ...ROUTES.guests.remove(id) }),
      invalidatesTags: [{ type: 'People', id: 'GUEST_LIST' }],
    }),
    startGuestExport: build.mutation<unknown, void>({
      query: () => ({ ...ROUTES.exportManager.exportGuest }),
      invalidatesTags: [{ type: 'People', id: 'GUEST_LIST' }],
    }),
    // Queue a people export — POST export-manager/export-people; `status` picks
    // active (People tab) vs archived (Archived tab). Fire-and-forget, so no
    // cache invalidation: the list itself doesn't change.
    startPeopleExport: build.mutation<
      unknown,
      { status: CompanyUserStatus.ACTIVE | CompanyUserStatus.ARCHIVED }
    >({
      query: (body) => ({ ...ROUTES.exportManager.exportPeople, data: body }),
    }),
    // Bulk delete guests — POST /guests/delete-multiple { guestIds }.
    deleteGuests: build.mutation<unknown, { guestIds: string[] }>({
      query: (body) => ({ ...ROUTES.guests.deleteMultiple, data: body }),
      invalidatesTags: [{ type: 'People', id: 'GUEST_LIST' }],
    }),
    // Bulk action on selected company users — POST company-users/bulk-action.
    bulkAction: build.mutation<unknown, BulkActionInput>({
      query: (body) => ({ ...ROUTES.companyUsers.bulkAction, data: body }),
      invalidatesTags: [{ type: 'People', id: 'LIST' }],
    }),
    // Accept/reject pending requests or withdraw admin invites — POST
    // company-users/update-invite-status.
    updateInviteStatus: build.mutation<unknown, UpdateInviteStatusInput>({
      query: (body) => ({ ...ROUTES.companyUsers.updateInviteStatus, data: body }),
      invalidatesTags: [{ type: 'People', id: 'LIST' }],
    }),
    // Delete archived company users — POST company-users/bulk-delete.
    deleteCompanyUsers: build.mutation<unknown, DeleteCompanyUsersInput>({
      query: (body) => ({ ...ROUTES.companyUsers.bulkDelete, data: body }),
      invalidatesTags: [{ type: 'People', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetPeopleQuery,
  useGetGuestsQuery,
  useGetPersonDetailQuery,
  useUpdatePersonMutation,
  useCreatePersonMutation,
  useImportUsersMutation,
  useCreateGuestMutation,
  useGetGuestDetailQuery,
  useUpdateGuestMutation,
  useDeleteGuestMutation,
  useStartGuestExportMutation,
  useStartPeopleExportMutation,
  useDeleteGuestsMutation,
  useBulkActionMutation,
  useUpdateInviteStatusMutation,
  useDeleteCompanyUsersMutation,
} = peopleApi;
